import { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Stack, Chip, Checkbox, IconButton, Button,
  Table, TableBody, TableRow, TableCell, Collapse,
} from '@mui/material';
import AddIcon  from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import authFetch from '../utils/authFetch';
import { NAVY, GOLD, clickableA11y } from '../styles/ds';
import TaskDialog from './TaskDialog';

const PRIORITY_COLOR = {
  high:   { bg: '#fdecea', color: '#c62828' },
  medium: { bg: '#fff8e1', color: '#e65100' },
  low:    { bg: '#e8f5e9', color: '#2e7d32' },
};
const PRIORITY_LABEL = { high: 'גבוהה', medium: 'בינונית', low: 'נמוכה' };

const parseDue = (iso) => {
  if (!iso) return null;
  const s = String(iso).split('T')[0];
  const [y, m, d] = s.split('-').map(Number);
  return (y && m && d) ? new Date(y, m - 1, d) : null;
};

const fmtDate = (iso) => {
  const dt = parseDue(iso);
  return dt ? dt.toLocaleDateString('he-IL') : null;
};

const dueDateStyle = (due) => {
  const dt = parseDue(due);
  if (!dt) return {};
  const diff = Math.floor((dt - new Date().setHours(0,0,0,0)) / 86400000);
  if (diff < 0)  return { color: '#c62828', fontWeight: 700 };
  if (diff === 0) return { color: '#e65100', fontWeight: 700 };
  return {};
};

const EntityTasksPanel = ({ entityType, entityId }) => {
  const [tasks,      setTasks]      = useState([]);
  const [expanded,   setExpanded]   = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing,    setEditing]    = useState(null);

  const load = useCallback(() => {
    if (!entityId) return;
    const f = JSON.stringify({ entity_type: entityType, entity_id: entityId, status: 'pending' });
    authFetch(`/tasks?filter=${encodeURIComponent(f)}&_start=0&_end=50`)
      .then(r => r.json())
      .then(d => setTasks(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, [entityType, entityId]);

  useEffect(() => { load(); }, [load]);

  const handleToggle = async (task) => {
    const newStatus = task.status === 'done' ? 'pending' : 'done';
    await authFetch(`/tasks/${task.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...task, status: newStatus }),
    });
    load();
  };

  const handleSaved = () => { setDialogOpen(false); setEditing(null); load(); };

  const pendingCount = tasks.filter(t => t.status === 'pending').length;

  return (
    <Box mb={3}>
      {/* Header */}
      <Stack className="crm-header" direction="row" justifyContent="space-between" alignItems="center"
        onClick={() => setExpanded(e => !e)}
        {...clickableA11y(() => setExpanded(e => !e), { ariaLabel: 'משימות ותזכורות — הצג/הסתר', asButton: true })}
        aria-expanded={expanded}
        sx={{ cursor: 'pointer', mb: expanded ? 1.5 : 0 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography fontWeight={700} color="primary.main" fontSize={15}>
            משימות ותזכורות
          </Typography>
          {pendingCount > 0 && (
            <Chip label={pendingCount} size="small"
              sx={{ height: 20, fontSize: 11, fontWeight: 700, backgroundColor: '#fdecea', color: '#c62828' }} />
          )}
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Button size="small" startIcon={<AddIcon />}
            onClick={e => { e.stopPropagation(); setEditing(null); setDialogOpen(true); }}
            sx={{ color: NAVY, fontSize: 12, minWidth: 0 }}>
            הוסף
          </Button>
          <IconButton size="small" tabIndex={-1} aria-hidden="true">
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </Stack>
      </Stack>

      <Collapse in={expanded}>
        {tasks.length === 0 ? (
          <Typography fontSize={13} color="text.secondary" px={1}>אין משימות פתוחות</Typography>
        ) : (
          <Box className="crm-table-scroll" data-mobile-scroll tabIndex={0} role="region" aria-label="טבלה נגללת"><Table size="small" sx={{ '& td': { py: 0.8 } }}>
            <TableBody>
              {tasks.map(t => {
                const done = t.status === 'done';
                const pc   = PRIORITY_COLOR[t.priority] || PRIORITY_COLOR.medium;
                const due  = fmtDate(t.due_date);
                return (
                  <TableRow key={t.id} sx={{ opacity: done ? 0.5 : 1 }}>
                    <TableCell sx={{ width: 36, p: 0, pr: 0.5 }}>
                      <Checkbox size="small" checked={done} onChange={() => handleToggle(t)} />
                    </TableCell>
                    <TableCell>
                      <Typography fontSize={13} fontWeight={600}
                        sx={{ textDecoration: done ? 'line-through' : 'none' }}>
                        {t.title}
                      </Typography>
                      {t.description && (
                        <Typography fontSize={12} color="text.secondary">{t.description}</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ width: 70 }}>
                      <Chip label={PRIORITY_LABEL[t.priority] || ''} size="small"
                        sx={{ height: 18, fontSize: 10, fontWeight: 700, backgroundColor: pc.bg, color: pc.color }} />
                    </TableCell>
                    {due && (
                      <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap', ...dueDateStyle(t.due_date) }}>
                        {due}
                      </TableCell>
                    )}
                    <TableCell sx={{ width: 36, p: 0 }}>
                      <IconButton size="small" onClick={() => { setEditing(t); setDialogOpen(true); }} aria-label="עריכת משימה">
                        <EditIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table></Box>
        )}
      </Collapse>

      <TaskDialog
        open={dialogOpen}
        task={editing}
        onClose={() => { setDialogOpen(false); setEditing(null); }}
        onSaved={handleSaved}
        defaultEntity={editing ? undefined : { type: entityType, id: entityId }}
      />
    </Box>
  );
};

export default EntityTasksPanel;
